import {Injectable} from '@angular/core';
import {AggregatedDataPoint, DataType, MeasurementsResult, TimeRange} from '@flowmaps-com/flowmaps-typescriptmodels';
import {BehaviorSubject} from 'rxjs';
import {WEII_CLIMATE_NORMALIZATION} from './weii-climate-normalization';

export {WEII_CLIMATE_NORMALIZATION} from './weii-climate-normalization';

export const WEII_ENERGY_FACTORS = Object.freeze({
    electricityKwhPerKwh: 1,
    gasKwhPerM3: 9.78,
    heatKwhPerGj: 278,
    coldKwhPerGj: 278
});

// WEii protocol 3.1, table 1. Keep physical conversion separate from score weighting.
export const WEII_THERMAL_WEIGHTS = Object.freeze({heat: 0.33, cold: 0.10});

export enum WeiiCalculationOptionId {
    ExcludeEvCharging = 'excludeEvCharging',
    IncludeOnsiteGeneration = 'includeOnsiteGeneration',
    NormalizeClimateYear = 'normalizeClimateYear'
}

export type WeiiCalculationOptionsState = Record<WeiiCalculationOptionId, boolean>;
export type WeiiCalculationOptionAvailability = Record<WeiiCalculationOptionId, boolean>;

export interface WeiiCalculationOptionCoverage {
    available: number;
    total: number;
}

export type WeiiCalculationOptionCoverageMap = Record<WeiiCalculationOptionId, WeiiCalculationOptionCoverage>;

export const DEFAULT_WEII_CALCULATION_OPTIONS: WeiiCalculationOptionsState = Object.freeze({
    [WeiiCalculationOptionId.ExcludeEvCharging]: false,
    [WeiiCalculationOptionId.IncludeOnsiteGeneration]: false,
    [WeiiCalculationOptionId.NormalizeClimateYear]: false
});

export interface WeiiCalculationInput {
    electricityImportKwh: number;
    electricityExportKwh: number;
    grossProductionKwh?: number;
    grossProductionDataComplete?: boolean;
    grossProductionDataReliable?: boolean;
    evConsumptionKwh?: number;
    gasM3?: number;
    heatGj?: number;
    coldGj?: number;
    climateNormalizationDegreeDays?: number;
    climateDataComplete?: boolean;
    floorAreaM2: number;
    isFullElectric: boolean;
    calculationOptions?: Partial<WeiiCalculationOptionsState>;
    /** @deprecated Use calculationOptions.excludeEvCharging. */
    applyEvCorrection?: boolean;
    calculationPeriod?: TimeRange;
    buildingCategory?: string;
    parisProofThreshold?: number;
    calculationTimestamp?: string;
}

export type WeiiWarningCode = 'MISSING_FLOOR_AREA' | 'INVALID_EV_CONSUMPTION'
    | 'EV_EXCEEDS_NET_ELECTRICITY' | 'INCOMPLETE_GROSS_PRODUCTION_DATA' | 'INVALID_GROSS_PRODUCTION'
    | 'UNRELIABLE_GROSS_PRODUCTION_DATA'
    | 'FULL_ELECTRIC_WITH_GAS' | 'FULL_ELECTRIC_WITH_THERMAL_DATA'
    | 'INCOMPLETE_CLIMATE_DATA' | 'INVALID_CLIMATE_DATA';

export interface WeiiWarning {
    code: WeiiWarningCode;
    message: string;
}

export interface WeiiCalculationResult {
    standardWeii: number | null;
    adjustedWeii: number | null;
    effectiveWeii: number | null;
    evCorrectionAvailable: boolean;
    evCorrectionApplied: boolean;
    onsiteGenerationAvailable: boolean;
    onsiteGenerationApplied: boolean;
    climateNormalizationAvailable: boolean;
    climateNormalizationApplied: boolean;
    optionAvailability: WeiiCalculationOptionAvailability;
    appliedOptions: WeiiCalculationOptionId[];
    excludedEvKwh: number;
    includedGrossProductionKwh: number;
    directlySelfConsumedElectricityKwh: number;
    excludedGasM3: number;
    excludedHeatGj: number;
    excludedColdGj: number;
    warnings: WeiiWarning[];
    components: {
        netElectricityKwh: number;
        grossProductionKwh: number;
        directlySelfConsumedElectricityKwh: number;
        adjustedElectricityKwh: number;
        correctedElectricityKwh: number;
        gasKwh: number;
        heatKwh: number;
        normalizedGasKwh: number;
        normalizedHeatKwh: number;
        climateCorrectionKwh: number;
        coldKwh: number;
        totalIncludedKwh: number;
    };
    audit: WeiiCalculationAudit;
}

export function summarizeWeiiCalculationOptionCoverage(
    calculations: WeiiCalculationResult[]
): WeiiCalculationOptionCoverageMap {
    return Object.values(WeiiCalculationOptionId).reduce((coverage, optionId) => ({
        ...coverage,
        [optionId]: {
            available: calculations.filter(calculation => calculation.optionAvailability[optionId]).length,
            total: calculations.length
        }
    }), {} as WeiiCalculationOptionCoverageMap);
}

export interface WeiiCalculationAudit {
    calculation_period: TimeRange | null;
    gross_electricity_import_kwh: number;
    electricity_export_kwh: number;
    net_electricity_kwh: number;
    gross_electricity_production_kwh: number;
    directly_self_consumed_electricity_kwh: number;
    adjusted_electricity_component_kwh: number;
    onsite_generation_applied: boolean;
    ev_consumption_kwh: number;
    ev_correction_applied: boolean;
    calculation_options: WeiiCalculationOptionsState;
    applied_calculation_options: WeiiCalculationOptionId[];
    gas_m3: number;
    gas_kwh_equivalent: number;
    gas_included_in_score: boolean;
    heat_gj: number;
    heat_kwh_equivalent: number;
    heat_weight: number;
    heat_weighted_kwh: number;
    heat_included_in_score: boolean;
    climate_normalization_applied: boolean;
    climate_normalization_degree_days: number;
    climate_reference_degree_days: number;
    climate_heating_share: number;
    climate_correction_factor: number;
    climate_correction_kwh: number;
    normalized_gas_kwh: number;
    normalized_heat_kwh: number;
    cold_gj: number;
    cold_kwh_equivalent: number;
    cold_weight: number;
    cold_weighted_kwh: number;
    cold_included_in_score: boolean;
    is_full_electric: boolean;
    usable_floor_area_m2: number;
    standard_weii: number | null;
    adjusted_weii: number | null;
    building_category: string | null;
    paris_proof_threshold: number | null;
    calculation_timestamp: string;
}

export interface WeiiMeasurementInput {
    measurements: MeasurementsResult[];
    floorAreaM2: number;
    isFullElectric: boolean;
    calculationOptions?: Partial<WeiiCalculationOptionsState>;
    /** @deprecated Use calculationOptions.excludeEvCharging. */
    applyEvCorrection?: boolean;
    calculationPeriod?: TimeRange;
    buildingCategory?: string;
    parisProofThreshold?: number;
}

@Injectable({providedIn: 'root'})
export class WeiiCalculationService {
    private static readonly settingsKey = 'weii-calculation-options';
    private readonly optionsSubject = new BehaviorSubject<WeiiCalculationOptionsState>(this.loadOptions());

    readonly options$ = this.optionsSubject.asObservable();

    get options(): WeiiCalculationOptionsState {
        return this.optionsSubject.value;
    }

    isOptionEnabled(optionId: WeiiCalculationOptionId): boolean {
        return this.options[optionId];
    }

    setOptionEnabled(optionId: WeiiCalculationOptionId, value: boolean): void {
        const options = {...this.options, [optionId]: value};
        localStorage.setItem(WeiiCalculationService.settingsKey, JSON.stringify({options}));
        this.optionsSubject.next(options);
    }

    /** @deprecated Use options$. */
    readonly applyEvCorrection$ = this.options$;

    /** @deprecated Use isOptionEnabled(ExcludeEvCharging). */
    get applyEvCorrection(): boolean {
        return this.isOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging);
    }

    /** @deprecated Use setOptionEnabled(ExcludeEvCharging, value). */
    setApplyEvCorrection(value: boolean): void {
        this.setOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging, value);
    }

    calculate(input: WeiiCalculationInput): WeiiCalculationResult {
        const electricityImportKwh = finiteOrZero(input.electricityImportKwh);
        const electricityExportKwh = finiteOrZero(input.electricityExportKwh);
        const grossProductionKwh = finiteOrZero(input.grossProductionKwh);
        const evConsumptionKwh = finiteOrZero(input.evConsumptionKwh);
        const gasM3 = finiteOrZero(input.gasM3);
        const heatGj = finiteOrZero(input.heatGj);
        const coldGj = finiteOrZero(input.coldGj);
        const climateDegreeDays = finiteOrZero(input.climateNormalizationDegreeDays);
        const floorAreaM2 = finiteOrZero(input.floorAreaM2);
        const warnings: WeiiWarning[] = [];
        const calculationOptions = resolveOptions(input.calculationOptions, input.applyEvCorrection);

        const netElectricityKwh = electricityImportKwh - electricityExportKwh;
        const convertedGasKwh = gasM3 * WEII_ENERGY_FACTORS.gasKwhPerM3;
        const gasKwh = input.isFullElectric ? 0 : convertedGasKwh;
        const convertedHeatKwh = heatGj * WEII_ENERGY_FACTORS.heatKwhPerGj;
        const convertedColdKwh = coldGj * WEII_ENERGY_FACTORS.coldKwhPerGj;
        const heatKwh = input.isFullElectric ? 0 : convertedHeatKwh * WEII_THERMAL_WEIGHTS.heat;
        const coldKwh = input.isFullElectric ? 0 : convertedColdKwh * WEII_THERMAL_WEIGHTS.cold;
        const hasClimateSensitiveEnergy = gasKwh > 0 || heatKwh > 0;
        const climateDataIsValid = input.climateNormalizationDegreeDays != null
            && Number.isFinite(input.climateNormalizationDegreeDays) && input.climateNormalizationDegreeDays > 0;
        const climateNormalizationAvailable = !input.isFullElectric && hasClimateSensitiveEnergy
            && input.climateDataComplete === true && climateDataIsValid;
        const climateReferenceDegreeDays = WEII_CLIMATE_NORMALIZATION.referenceHeatingDegreeDays
            * completeCalendarYearCount(input.calculationPeriod);
        const climateCorrectionFactor = climateNormalizationAvailable
            ? climateReferenceDegreeDays / climateDegreeDays : 1;
        const normalizedGasKwh = gasKwh * ((1 - WEII_CLIMATE_NORMALIZATION.heatingShare)
            + WEII_CLIMATE_NORMALIZATION.heatingShare * climateCorrectionFactor);
        const normalizedHeatKwh = heatKwh * ((1 - WEII_CLIMATE_NORMALIZATION.heatingShare)
            + WEII_CLIMATE_NORMALIZATION.heatingShare * climateCorrectionFactor);
        const climateCorrectionKwh = climateNormalizationAvailable
            ? normalizedGasKwh + normalizedHeatKwh - gasKwh - heatKwh : 0;

        if (floorAreaM2 <= 0) {
            warnings.push({code: 'MISSING_FLOOR_AREA', message: 'No valid usable floor area is available.'});
        }
        if (input.isFullElectric && gasM3 > 0) {
            warnings.push({
                code: 'FULL_ELECTRIC_WITH_GAS',
                message: 'This location is marked as full-electric. Available gas values are therefore excluded from the score.'
            });
        }
        if (input.isFullElectric && (heatGj > 0 || coldGj > 0)) {
            warnings.push({
                code: 'FULL_ELECTRIC_WITH_THERMAL_DATA',
                message: 'This location is marked as full-electric. Available heat and cooling values in GJ are therefore excluded from the score.'
            });
        }

        const grossProductionIsValid = input.grossProductionKwh != null
            && Number.isFinite(input.grossProductionKwh) && input.grossProductionKwh >= 0;
        const onsiteGenerationAvailable = grossProductionIsValid
            && input.grossProductionDataComplete === true
            && input.grossProductionDataReliable === true;
        const includeOnsiteGenerationRequested = calculationOptions[WeiiCalculationOptionId.IncludeOnsiteGeneration];

        if (includeOnsiteGenerationRequested && input.grossProductionDataComplete !== true) {
            warnings.push({
                code: 'INCOMPLETE_GROSS_PRODUCTION_DATA',
                message: 'No complete gross electricity production data is available for the selected period.'
            });
        } else if (includeOnsiteGenerationRequested && !grossProductionIsValid) {
            warnings.push({
                code: 'INVALID_GROSS_PRODUCTION',
                message: 'The measured gross electricity production is invalid. Onsite generation was not included.'
            });
        } else if (includeOnsiteGenerationRequested && input.grossProductionDataReliable !== true) {
            warnings.push({
                code: 'UNRELIABLE_GROSS_PRODUCTION_DATA',
                message: 'The gross electricity production data is not marked as reliable. Onsite generation was not included.'
            });
        }

        const electricityBeforeEvKwh = netElectricityKwh
            + (includeOnsiteGenerationRequested && onsiteGenerationAvailable ? grossProductionKwh : 0);
        const evIsValid = input.evConsumptionKwh == null || (Number.isFinite(input.evConsumptionKwh) && input.evConsumptionKwh >= 0);
        const evCorrectionAvailable = input.evConsumptionKwh != null && evIsValid
            && evConsumptionKwh <= electricityBeforeEvKwh;
        const optionAvailability: WeiiCalculationOptionAvailability = {
            [WeiiCalculationOptionId.ExcludeEvCharging]: evCorrectionAvailable,
            [WeiiCalculationOptionId.IncludeOnsiteGeneration]: onsiteGenerationAvailable,
            [WeiiCalculationOptionId.NormalizeClimateYear]: climateNormalizationAvailable
        };
        const excludeEvChargingRequested = calculationOptions[WeiiCalculationOptionId.ExcludeEvCharging];
        const normalizeClimateRequested = calculationOptions[WeiiCalculationOptionId.NormalizeClimateYear];

        if (normalizeClimateRequested && input.climateDataComplete !== true) {
            warnings.push({
                code: 'INCOMPLETE_CLIMATE_DATA',
                message: 'No complete climate data is available for the selected calculation year.'
            });
        } else if (normalizeClimateRequested && !input.isFullElectric && hasClimateSensitiveEnergy && !climateDataIsValid) {
            warnings.push({
                code: 'INVALID_CLIMATE_DATA',
                message: 'The climate data is invalid. Climate normalization was not applied.'
            });
        }

        if (excludeEvChargingRequested && !evIsValid) {
            warnings.push({
                code: 'INVALID_EV_CONSUMPTION',
                message: 'The measured EV charging consumption is invalid. The EV correction was not applied.'
            });
        } else if (excludeEvChargingRequested && evConsumptionKwh > electricityBeforeEvKwh) {
            warnings.push({
                code: 'EV_EXCEEDS_NET_ELECTRICITY',
                message: 'EV charging consumption exceeds net electricity consumption. The EV correction was not applied.'
            });
        }

        const standardTotalKwh = netElectricityKwh + gasKwh + heatKwh + coldKwh;
        const pipelineResult = applyCalculationOptions({
            electricityKwh: netElectricityKwh,
            totalKwh: standardTotalKwh,
            excludedEvKwh: 0,
            includedGrossProductionKwh: 0
        }, calculationOptions, optionAvailability, {evConsumptionKwh, grossProductionKwh, climateCorrectionKwh});
        const evCorrectionApplied = pipelineResult.appliedOptions.includes(WeiiCalculationOptionId.ExcludeEvCharging);
        const onsiteGenerationApplied = pipelineResult.appliedOptions.includes(WeiiCalculationOptionId.IncludeOnsiteGeneration);
        const climateNormalizationApplied = pipelineResult.appliedOptions.includes(WeiiCalculationOptionId.NormalizeClimateYear);
        const correctedElectricityKwh = pipelineResult.electricityKwh;
        const directlySelfConsumedElectricityKwh = onsiteGenerationAvailable
            ? Math.max(0, grossProductionKwh - electricityExportKwh) : 0;
        const adjustedTotalKwh = pipelineResult.totalKwh;
        const standardWeii = floorAreaM2 > 0 ? standardTotalKwh / floorAreaM2 : null;
        const adjustedWeii = floorAreaM2 > 0 ? adjustedTotalKwh / floorAreaM2 : null;
        const effectiveWeii = pipelineResult.appliedOptions.length > 0 ? adjustedWeii : standardWeii;

        const audit: WeiiCalculationAudit = {
            calculation_period: input.calculationPeriod || null,
            gross_electricity_import_kwh: electricityImportKwh,
            electricity_export_kwh: electricityExportKwh,
            net_electricity_kwh: netElectricityKwh,
            gross_electricity_production_kwh: grossProductionKwh,
            directly_self_consumed_electricity_kwh: directlySelfConsumedElectricityKwh,
            adjusted_electricity_component_kwh: correctedElectricityKwh,
            onsite_generation_applied: onsiteGenerationApplied,
            ev_consumption_kwh: evConsumptionKwh,
            ev_correction_applied: evCorrectionApplied,
            calculation_options: calculationOptions,
            applied_calculation_options: pipelineResult.appliedOptions,
            gas_m3: gasM3,
            gas_kwh_equivalent: convertedGasKwh,
            gas_included_in_score: !input.isFullElectric && gasM3 > 0,
            heat_gj: heatGj,
            heat_kwh_equivalent: convertedHeatKwh,
            heat_weight: WEII_THERMAL_WEIGHTS.heat,
            heat_weighted_kwh: heatKwh,
            heat_included_in_score: !input.isFullElectric && heatGj > 0,
            climate_normalization_applied: climateNormalizationApplied,
            climate_normalization_degree_days: climateDegreeDays,
            climate_reference_degree_days: climateReferenceDegreeDays,
            climate_heating_share: WEII_CLIMATE_NORMALIZATION.heatingShare,
            climate_correction_factor: climateCorrectionFactor,
            climate_correction_kwh: climateNormalizationApplied ? climateCorrectionKwh : 0,
            normalized_gas_kwh: climateNormalizationApplied ? normalizedGasKwh : gasKwh,
            normalized_heat_kwh: climateNormalizationApplied ? normalizedHeatKwh : heatKwh,
            cold_gj: coldGj,
            cold_kwh_equivalent: convertedColdKwh,
            cold_weight: WEII_THERMAL_WEIGHTS.cold,
            cold_weighted_kwh: coldKwh,
            cold_included_in_score: !input.isFullElectric && coldGj > 0,
            is_full_electric: input.isFullElectric,
            usable_floor_area_m2: floorAreaM2,
            standard_weii: standardWeii,
            adjusted_weii: adjustedWeii,
            building_category: input.buildingCategory || null,
            paris_proof_threshold: input.parisProofThreshold ?? null,
            calculation_timestamp: input.calculationTimestamp || new Date().toISOString()
        };

        return {
            standardWeii,
            adjustedWeii,
            effectiveWeii,
            evCorrectionAvailable,
            evCorrectionApplied,
            onsiteGenerationAvailable,
            onsiteGenerationApplied,
            climateNormalizationAvailable,
            climateNormalizationApplied,
            optionAvailability,
            appliedOptions: pipelineResult.appliedOptions,
            excludedEvKwh: pipelineResult.excludedEvKwh,
            includedGrossProductionKwh: pipelineResult.includedGrossProductionKwh,
            directlySelfConsumedElectricityKwh,
            excludedGasM3: input.isFullElectric ? gasM3 : 0,
            excludedHeatGj: input.isFullElectric ? heatGj : 0,
            excludedColdGj: input.isFullElectric ? coldGj : 0,
            warnings,
            components: {
                netElectricityKwh,
                grossProductionKwh,
                directlySelfConsumedElectricityKwh,
                adjustedElectricityKwh: correctedElectricityKwh,
                correctedElectricityKwh,
                gasKwh,
                heatKwh,
                normalizedGasKwh: climateNormalizationApplied ? normalizedGasKwh : gasKwh,
                normalizedHeatKwh: climateNormalizationApplied ? normalizedHeatKwh : heatKwh,
                climateCorrectionKwh: climateNormalizationApplied ? climateCorrectionKwh : 0,
                coldKwh,
                totalIncludedKwh: pipelineResult.totalKwh
            },
            audit
        };
    }

    calculateMeasurements(input: WeiiMeasurementInput): WeiiCalculationResult {
        const corePoints = pointsFor(input.measurements, [
            DataType.electricityConsumption, DataType.electricityConsumptionOffPeak,
            DataType.electricityFeedIn, DataType.electricityFeedInOffPeak,
            DataType.gasConsumption, DataType.heatConsumption, DataType.coolingConsumption
        ]);
        const calculationPeriod = input.calculationPeriod || rangeOf(corePoints);
        const evPoints = pointsFor(input.measurements, [DataType.chargePointConsumption]);
        const grossProductionPoints = pointsFor(input.measurements, [DataType.electricityGrossProduction]);
        const climateDegreeDayPoints = pointsFor(input.measurements, [DataType.climateNormalizationDegreeDays]);

        return this.calculate({
            electricityImportKwh: sum(input.measurements, [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak]),
            electricityExportKwh: sum(input.measurements, [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak]),
            grossProductionKwh: grossProductionPoints.length > 0
                ? grossProductionPoints.reduce((total, point) => total + Number(point.value), 0) : undefined,
            grossProductionDataComplete: calculationPeriod != null
                && everyMeasurementSeriesCoversPeriod(input.measurements, DataType.electricityGrossProduction, calculationPeriod),
            // This canonical data type represents actual production-meter or inverter measurements. It is never inferred
            // from import/export. A future source-quality flag can narrow this contract without changing the pipeline.
            grossProductionDataReliable: grossProductionPoints.length > 0
                && grossProductionPoints.every(point => Number.isFinite(Number(point.value)) && Number(point.value) >= 0),
            evConsumptionKwh: evPoints.length > 0 ? evPoints.reduce((total, point) => total + Number(point.value), 0) : undefined,
            gasM3: sum(input.measurements, [DataType.gasConsumption]),
            heatGj: sum(input.measurements, [DataType.heatConsumption]),
            coldGj: sum(input.measurements, [DataType.coolingConsumption]),
            climateNormalizationDegreeDays: climateDegreeDayPoints.length > 0
                ? climateDegreeDayPoints.reduce((total, point) => total + Number(point.value), 0) : undefined,
            climateDataComplete: calculationPeriod != null && isCompleteCalendarYearPeriod(calculationPeriod)
                && coversPeriod(climateDegreeDayPoints, calculationPeriod),
            floorAreaM2: input.floorAreaM2,
            isFullElectric: input.isFullElectric,
            calculationOptions: input.calculationOptions,
            applyEvCorrection: input.applyEvCorrection,
            calculationPeriod,
            buildingCategory: input.buildingCategory,
            parisProofThreshold: input.parisProofThreshold
        });
    }

    private loadOptions(): WeiiCalculationOptionsState {
        try {
            const stored = JSON.parse(localStorage.getItem(WeiiCalculationService.settingsKey) || '{}');
            return resolveOptions(stored.options, stored.applyEvCorrection);
        } catch {
            return {...DEFAULT_WEII_CALCULATION_OPTIONS};
        }
    }
}

interface WeiiCalculationPipelineState {
    electricityKwh: number;
    totalKwh: number;
    excludedEvKwh: number;
    includedGrossProductionKwh: number;
    appliedOptions?: WeiiCalculationOptionId[];
}

interface WeiiCalculationPipelineContext {
    evConsumptionKwh: number;
    grossProductionKwh: number;
    climateCorrectionKwh: number;
}

interface WeiiCalculationStep {
    optionId: WeiiCalculationOptionId;
    apply: (state: WeiiCalculationPipelineState, context: WeiiCalculationPipelineContext) => WeiiCalculationPipelineState;
}

const WEII_CALCULATION_STEPS: WeiiCalculationStep[] = [{
    optionId: WeiiCalculationOptionId.IncludeOnsiteGeneration,
    apply: (state, context) => ({
        ...state,
        electricityKwh: state.electricityKwh + context.grossProductionKwh,
        totalKwh: state.totalKwh + context.grossProductionKwh,
        includedGrossProductionKwh: context.grossProductionKwh
    })
}, {
    optionId: WeiiCalculationOptionId.ExcludeEvCharging,
    apply: (state, context) => ({
        ...state,
        electricityKwh: state.electricityKwh - context.evConsumptionKwh,
        totalKwh: state.totalKwh - context.evConsumptionKwh,
        excludedEvKwh: context.evConsumptionKwh
    })
}, {
    optionId: WeiiCalculationOptionId.NormalizeClimateYear,
    apply: (state, context) => ({
        ...state,
        totalKwh: state.totalKwh + context.climateCorrectionKwh
    })
}];

function applyCalculationOptions(initialState: WeiiCalculationPipelineState,
                                 options: WeiiCalculationOptionsState,
                                 availability: WeiiCalculationOptionAvailability,
                                 context: WeiiCalculationPipelineContext): Required<WeiiCalculationPipelineState> {
    const appliedOptions: WeiiCalculationOptionId[] = [];
    const state = WEII_CALCULATION_STEPS.reduce((current, step) => {
        if (!options[step.optionId] || !availability[step.optionId]) return current;
        appliedOptions.push(step.optionId);
        return step.apply(current, context);
    }, initialState);
    return {...state, appliedOptions};
}

function resolveOptions(options?: Partial<WeiiCalculationOptionsState>, legacyApplyEvCorrection?: boolean): WeiiCalculationOptionsState {
    const resolved = {...DEFAULT_WEII_CALCULATION_OPTIONS, ...(options || {})};
    if (legacyApplyEvCorrection != null) {
        resolved[WeiiCalculationOptionId.ExcludeEvCharging] = legacyApplyEvCorrection;
    }
    return resolved;
}

function finiteOrZero(value: number | undefined): number {
    return value != null && Number.isFinite(value) ? value : 0;
}

function pointsFor(results: MeasurementsResult[], types: DataType[]): AggregatedDataPoint[] {
    return (results || []).flatMap(result => types.flatMap(type => result.measurements?.[type] || []));
}

function sum(results: MeasurementsResult[], types: DataType[]): number {
    return pointsFor(results, types).reduce((total, point) => total + Number(point.value), 0);
}

function rangeOf(points: AggregatedDataPoint[]): TimeRange | undefined {
    if (points.length === 0) return undefined;
    return {
        start: points.reduce((start, point) => point.timeRange.start < start ? point.timeRange.start : start, points[0].timeRange.start),
        end: points.reduce((end, point) => point.timeRange.end > end ? point.timeRange.end : end, points[0].timeRange.end)
    };
}

function coversPeriod(points: AggregatedDataPoint[], period: TimeRange): boolean {
    if (points.length === 0) return false;
    const sorted = [...points]
        .filter(point => point.timeRange.end > period.start && point.timeRange.start < period.end)
        .sort((a, b) => a.timeRange.start.localeCompare(b.timeRange.start));
    if (sorted.length === 0 || sorted[0].timeRange.start > period.start) return false;
    let coveredUntil = period.start;
    for (const point of sorted) {
        if (point.timeRange.start > coveredUntil) return false;
        if (point.timeRange.end > coveredUntil) coveredUntil = point.timeRange.end;
        if (coveredUntil >= period.end) return true;
    }
    return false;
}

function everyMeasurementSeriesCoversPeriod(results: MeasurementsResult[], type: DataType, period: TimeRange): boolean {
    const series = (results || []).map(result => result.measurements?.[type] || []).filter(points => points.length > 0);
    return series.length > 0 && series.every(points => coversPeriod(points, period));
}

function isCompleteCalendarYearPeriod(period: TimeRange): boolean {
    const start = new Date(period.start);
    const end = new Date(period.end);
    return Number.isFinite(start.getTime()) && Number.isFinite(end.getTime())
        && start.getMonth() === 0 && start.getDate() === 1 && start.getHours() === 0
        && end.getMonth() === 0 && end.getDate() === 1 && end.getHours() === 0
        && end.getFullYear() > start.getFullYear();
}

function completeCalendarYearCount(period?: TimeRange): number {
    if (!period || !isCompleteCalendarYearPeriod(period)) return 1;
    return Math.max(1, new Date(period.end).getFullYear() - new Date(period.start).getFullYear());
}
